export const LAZY_MODULE_LOADED_EVENT = 'truevined:lazy-module-loaded';

type LocationLike = Pick<Location, 'hash' | 'href' | 'origin' | 'pathname' | 'search'>;

export function getRouteKey(location: Pick<Location, 'hash' | 'pathname' | 'search'>): string {
  return `${location.pathname}${location.search}${location.hash}`;
}

/**
 * The internal route a click is asking for, or null when the click must be
 * left to the browser or to React Router. Used to turn link clicks into full
 * page navigations once the tab is known to be running an old build.
 */
export function getStaleUpdateNavigationTarget(
  event: MouseEvent,
  currentLocation: LocationLike = window.location,
): string | null {
  if (
    event.defaultPrevented ||
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.altKey ||
    event.shiftKey
  ) {
    return null;
  }

  const target = event.target;
  if (!(target instanceof Element)) return null;

  const anchor = target.closest<HTMLAnchorElement>('a[href]');
  if (!anchor || anchor.hasAttribute('download') || (anchor.target && anchor.target !== '_self')) {
    return null;
  }

  const rawHref = anchor.getAttribute('href')?.trim();
  if (!rawHref || rawHref.startsWith('#') || rawHref.startsWith('mailto:') || rawHref.startsWith('tel:')) {
    return null;
  }

  let url: URL;
  try {
    url = new URL(anchor.href, currentLocation.href);
  } catch {
    return null;
  }

  if (url.origin !== currentLocation.origin) return null;
  if (url.pathname === currentLocation.pathname && url.search === currentLocation.search) return null;

  return `${url.pathname}${url.search}${url.hash}`;
}
